import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from '../components/ui/sonner';
import { ArrowLeft } from 'lucide-react';

// Floating animated orb component
const FloatingOrb = ({ size, color, initialX, initialY, delay, duration }) => {
  return (
    <div
      className="absolute rounded-full"
      style={{
        width: size,
        height: size,
        left: initialX,
        top: initialY,
        background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
        filter: 'blur(60px)',
        animation: `float-${delay} ${duration}s ease-in-out infinite, glow ${duration * 0.5}s ease-in-out infinite alternate`,
        animationDelay: `${delay}s`,
      }}
    />
  );
};

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Login successful!');
      navigate('/select-module');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#0d1b2a]">
      {/* CSS Keyframes for floating + glowing animations */}
      <style>{`
        @keyframes float-0 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(30px, -40px) scale(1.1); }
          50% { transform: translate(-20px, 20px) scale(0.95); }
          75% { transform: translate(40px, 30px) scale(1.05); }
        }
        @keyframes float-1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(-40px, 30px) scale(1.08); }
          50% { transform: translate(30px, -30px) scale(0.92); }
          75% { transform: translate(-20px, -40px) scale(1.12); }
        }
        @keyframes float-2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(50px, 20px) scale(1.15); }
          66% { transform: translate(-30px, -50px) scale(0.9); }
        }
        @keyframes float-3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          20% { transform: translate(-50px, -20px) scale(1.1); }
          40% { transform: translate(20px, 40px) scale(0.95); }
          60% { transform: translate(40px, -30px) scale(1.05); }
          80% { transform: translate(-30px, 50px) scale(1.12); }
        }
        @keyframes float-4 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(60px, -40px) scale(1.2); }
        }
        @keyframes float-5 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          30% { transform: translate(-40px, 50px) scale(1.1); }
          70% { transform: translate(50px, -20px) scale(0.9); }
        }
        @keyframes glow {
          0% { opacity: 0.5; }
          100% { opacity: 0.9; }
        }
      `}</style>

      {/* Animated floating & glowing orbs - DOMINANT */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Large primary orbs */}
        <div
          className="absolute rounded-full"
          style={{
            width: '600px',
            height: '600px',
            left: '-10%',
            top: '5%',
            background: 'radial-gradient(circle, rgba(61, 106, 159, 0.8) 0%, rgba(45, 74, 111, 0.4) 40%, transparent 70%)',
            filter: 'blur(40px)',
            animation: 'float-0 12s ease-in-out infinite, glow 4s ease-in-out infinite alternate',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: '550px',
            height: '550px',
            right: '-5%',
            bottom: '10%',
            background: 'radial-gradient(circle, rgba(77, 122, 191, 0.75) 0%, rgba(45, 90, 140, 0.35) 40%, transparent 70%)',
            filter: 'blur(45px)',
            animation: 'float-1 14s ease-in-out infinite, glow 5s ease-in-out infinite alternate',
            animationDelay: '1s',
          }}
        />
        {/* Medium accent orbs */}
        <div
          className="absolute rounded-full"
          style={{
            width: '400px',
            height: '400px',
            right: '20%',
            top: '15%',
            background: 'radial-gradient(circle, rgba(90, 159, 212, 0.7) 0%, rgba(61, 106, 159, 0.3) 50%, transparent 70%)',
            filter: 'blur(35px)',
            animation: 'float-2 10s ease-in-out infinite, glow 3s ease-in-out infinite alternate',
            animationDelay: '2s',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: '450px',
            height: '450px',
            left: '15%',
            bottom: '5%',
            background: 'radial-gradient(circle, rgba(42, 80, 128, 0.8) 0%, rgba(30, 58, 95, 0.4) 45%, transparent 70%)',
            filter: 'blur(50px)',
            animation: 'float-3 16s ease-in-out infinite, glow 6s ease-in-out infinite alternate',
            animationDelay: '0.5s',
          }}
        />
        {/* Smaller floating orbs for depth */}
        <div
          className="absolute rounded-full"
          style={{
            width: '300px',
            height: '300px',
            left: '50%',
            top: '60%',
            background: 'radial-gradient(circle, rgba(122, 184, 232, 0.6) 0%, transparent 60%)',
            filter: 'blur(30px)',
            animation: 'float-4 8s ease-in-out infinite, glow 2.5s ease-in-out infinite alternate',
            animationDelay: '3s',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: '350px',
            height: '350px',
            right: '35%',
            top: '5%',
            background: 'radial-gradient(circle, rgba(45, 90, 140, 0.65) 0%, transparent 65%)',
            filter: 'blur(35px)',
            animation: 'float-5 11s ease-in-out infinite, glow 4s ease-in-out infinite alternate',
            animationDelay: '1.5s',
          }}
        />
        {/* Central glow for emphasis */}
        <div
          className="absolute rounded-full"
          style={{
            width: '500px',
            height: '500px',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, rgba(61, 106, 159, 0.5) 0%, transparent 60%)',
            filter: 'blur(60px)',
            animation: 'glow 5s ease-in-out infinite alternate',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center p-4 w-full max-w-md">
        {/* Back to landing */}
        <Link
          to="/"
          className="self-start mb-4 flex items-center gap-1.5 text-[#7a9cc6] hover:text-white text-xs transition-colors"
          data-testid="back-to-landing-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </Link>

        {/* Combined Card - Logo on top, Form below - COMPACT */}
        <div 
          className="w-full rounded-2xl overflow-hidden"
          style={{ 
            boxShadow: '0 0 30px 6px rgba(61, 106, 159, 0.4), 0 0 60px 12px rgba(61, 106, 159, 0.2)'
          }}
        >
          {/* Logo Block - White (compact) */}
          <div className="bg-white py-3 px-4 flex items-center justify-center">
            <img 
              src="/logos/logo-navy-wolf.png" 
              alt="Strategy Wolves" 
              className="w-full max-w-[180px] h-auto object-contain"
            />
          </div>
          
          {/* Subtle glowing line between sections */}
          <div 
            className="h-px w-full"
            style={{ 
              background: 'linear-gradient(90deg, transparent 0%, rgba(61, 106, 159, 0.5) 20%, rgba(61, 106, 159, 0.8) 50%, rgba(61, 106, 159, 0.5) 80%, transparent 100%)',
              boxShadow: '0 2px 10px 0 rgba(61, 106, 159, 0.4)'
            }}
          />
          
          {/* Sign In Block - Navy (compact) */}
          <div className="bg-[#132438] px-6 py-4">
            {/* Welcome Text */}
            <div className="text-center mb-3">
              <h2 
                className="text-lg font-bold text-white"
                style={{ textShadow: '0 0 8px rgba(255,255,255,0.4), 0 0 16px rgba(255,255,255,0.2)' }}
              >
                Strategy Wolves
              </h2>
              <p className="text-[#7a9cc6] text-xs mt-0.5">Sign in to continue</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-2.5" data-testid="login-form">
              <div>
                <Label className="text-[#a3c4e8] text-xs">Email</Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                  className="mt-1 h-9 bg-[#0d1b2a]/60 border-[#1e3a5f]/50 text-white placeholder:text-[#5a7a9c] focus:border-[#3d6a9f] focus:ring-[#3d6a9f]/20"
                />
              </div>
              <div>
                <Label className="text-[#a3c4e8] text-xs">Password</Label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                  className="mt-1 h-9 bg-[#0d1b2a]/60 border-[#1e3a5f]/50 text-white placeholder:text-[#5a7a9c] focus:border-[#3d6a9f] focus:ring-[#3d6a9f]/20"
                />
              </div>
              
              <Button
                type="submit"
                data-testid="login-submit-btn"
                className="w-full h-9 bg-gradient-to-r from-[#1e3a5f] via-[#2d4a6f] to-[#3d5a7f] hover:from-[#2d4a6f] hover:to-[#4d6a8f] text-white font-semibold shadow-lg shadow-[#1e3a5f]/30 transition-all"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </span>
                ) : 'Sign In'}
              </Button>
            </form>
            
            <div className="mt-3 text-center text-xs text-[#7a9cc6]">
              Don't have an account?{' '}
              <Link 
                to="/signup" 
                className="text-[#5a9fd4] font-semibold hover:text-[#7ab8e8] transition-colors"
              >
                Sign up
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-[#5a7a9c] text-xs">
          © 2026 Strategy Wolves. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Login;
